import React, {useEffect, useState, useMemo}		from 'react';

import Checkbox										from '../Checkbox';
import Modal										from '../Modal';
import Avatar										from '../Avatar';
import DevicesBadges								from '../Badge/DevicesBadges';
import Input										from '../Input';
import Icon											from '../Icon';
import Badge										from '../Badge';
import Select										from '../Select';

import {useAppSelector}								from '../../../app/hooks';
import {InputValues}								from '../Input/interface';
import {User}										from '../../../redux/user/interface';
import {LanguageType}								from '../../language/type';

import useWindowSize								from '../../functions/useWindowSize';
import {ft_formatUserName}							from '../../functions/format';
import {ft_search}									from '../../functions/search';
import {ft_selectDataString}						from '../../functions/selectData';
import {ft_checkUsersFilters, ft_sortUsers}			from '../../functions/filters';

import img_search									from '../../images/icon/search.svg';
import img_refresh									from '../../images/icon/refresh.svg';
import img_arrowDown								from '../../images/icon/arrow/down.svg';

import LANGUAGE										from '../../language';

import './ModalAssign.css';


interface Props {
	actualId?: string[],
	onChange?: (newUser: User, list?: string[]) => void
	onConfirm?: (newValue?: string) => void,
	onClose?: () => void
	load?: boolean
	modalRight?: boolean
	notModal?: boolean,
	multiple?: boolean,
	confirmText?: string,
	header?: string,
	forceList?: User[]
}


function ModalAssign(props: Props) {
	const users = useAppSelector(state => state.companyUsers.data);
	const lang = useAppSelector(state => state.settings.lang);

	const [search, set_search] = useState<InputValues>('');
	const [filterTags, set_filterTags] = useState<string[]>([]);
	const [sort, set_sort] = useState('');
	const [actual, set_actual] = useState<string[]>([]);

	const sizes = useWindowSize();
	const isComputer = sizes.width > 767;

	const useData = props.forceList || users;

	const allTags = useMemo(() => {
		const found: string[] = [];

		useData.forEach((user) => {
			user?.tags?.forEach((tag) => {
				if (!found.find(find => find === tag))
					found.push(tag);
			});
		});

		return found;
	}, [useData]);

	useEffect(() => set_actual(props.actualId || []), [props.actualId]);

	const TEXTE = LANGUAGE(lang);

	function renderUsers() {
		const render: JSX.Element[] = [];

		ft_sortUsers(useData, sort).forEach((userData, i) => {
			if (!userData || userData.data?.offboarding?.end || (search && !ft_search(ft_formatUserName(userData), search, true, true)))
				return;
			if (filterTags.length > 0 && !ft_checkUsersFilters([{key: 'tags', values: filterTags}], userData))
				return;

			const index = actual.findIndex(find => find === userData?._id);
			const renderTags:React.ReactElement[] = [];

			userData.tags?.forEach((tag, iTag) => {
				renderTags.push(
					<Badge key={iTag} size={'sm'} color={'gray'} text={tag} />
				);
			});

			render.push(
				<tr
					key={i}
					className={`modalAssignTableRow ${index !== -1 && 'selected'}`}
					onClick={() => {
						if (props.multiple) {
							const copy = [...actual];

							if (index === -1)
								copy.push(userData._id);
							else
								copy.splice(index, 1);

							if (props.onChange)
								props.onChange(userData, copy);

							return set_actual(copy);
						}
						if (props.onChange)
							props.onChange(userData);
						set_actual([userData._id]);
					}}
				>
					<td>
						<div>
							<Checkbox round={!props.multiple} active={index !== -1} />
							<Avatar user={userData} />
							<span className={'text-xs weight-500 gray-900'}>{ft_formatUserName(userData)}</span>
						</div>
					</td>
					<td>
						<span className={'text-xs weight-400 gray-600'}>{userData?.job}</span>
					</td>
					{isComputer &&
						<td>
							<DevicesBadges gap={4} userData={userData} />
						</td>
					}
					<td>
						<div className={'modalAssignTags'}>
							{renderTags}
						</div>
					</td>
				</tr>
			);
		});

		return render;
	}

	function renderTh(name: keyof LanguageType['global'], allowSort?: boolean, sortName?: string) {
		return (
			<th
				onClick={() => {
					if (!allowSort)
						return;
					if (sort === `${sortName || name}-AtoZ`)
						return set_sort(`${sortName || name}-ZtoA`);
					set_sort(`${sortName || name}-AtoZ`);
				}}
			>
				<div
					className={'flex'}
					style={{gap: 4, cursor: 'pointer'}}
				>
					{(props.multiple && name === 'name') &&
						<div onClick={e => e.stopPropagation()}>
							<Checkbox
								onChange={(checked) => {
									if (!checked)
										return set_actual([]);
									const copy = [...actual];

									useData.forEach((userData) => {
										if (userData && !actual.some(find => find === userData._id))
											copy.push(userData._id);
									});

									if (props.onChange)
										props.onChange(useData[0], copy);

									return set_actual(copy);
								}}
								active={actual.length === useData.length}
							/>
						</div>
					}
					{TEXTE.global[name]}
					{sort.startsWith(sortName || name) &&
						<Icon
							icon={img_arrowDown}
							className={`anim-rotate-${sort.endsWith('AtoZ') ? 'open' : 'close'}`}
						/>
					}
				</div>
			</th>
		);
	}

	function renderContent() {
		return (
			<>
				<Input
					value={search}
					before={<Icon icon={img_search} />}
					onChange={val => set_search(val as string)}
					placeholder={TEXTE.devices.modal.typeAName}
				/>

				<div className={'modalAssignTagsFilterContainer'}>
					<Select
						notFullWidth
						placeholder={TEXTE.global.tags}
						placeholderClassname={'text-sm'}
						data={ft_selectDataString(allTags)}
						checkboxChecked={value => !!filterTags.find(find => find === value)}
						onClickCheckbox={(value) => {
							const copy = [...filterTags];
							const index = copy.findIndex(find => find === value);

							if (index === -1)
								copy.push(value as string);
							else
								copy.splice(index, 1);

							set_filterTags(copy);
						}}
					/>

					{filterTags.length > 0 &&
						<Icon
							icon={img_refresh}
							popup={TEXTE.global.reset}
							onClick={() => set_filterTags([])}
						/>
					}
				</div>

				<div className={'modalAssignTableContainer'}>
					<table className={'modalAssignTable'}>
						<thead>
							<tr style={{textAlign: 'left'}}>
								{renderTh('name', true)}
								{renderTh('role', true, 'job')}
								{isComputer && renderTh('devices')}
								{renderTh('tags')}
							</tr>
						</thead>
						<tbody>
							{renderUsers()}
						</tbody>
					</table>
				</div>
			</>
		);
	}

	if (props.notModal)
		return <div className={'assignContainerNotModal'}>{renderContent()}</div>;

	let size = 778;

	if (props.modalRight)
		size = 840;

	return (
		<Modal
			onClose={props.onClose}
			confirmLoad={props.load}
			modalRight={props.modalRight}
			size={isComputer ? size : undefined}
			confirmButton={() => props.onConfirm && props.onConfirm(actual[0])}
			confirmButtonText={props.confirmText || TEXTE.contract.assignDevice}
			header={
				<div style={{flex: 1, textAlign: props.modalRight ? 'left' : 'center'}}>{props.header || TEXTE.contract.assignADevice}</div>
			}
		>
			{renderContent()}
		</Modal>
	);
}

export default ModalAssign;